/**
 * 検証タブの状態遷移を司る純関数 FSM。
 *
 * 完全な純関数: 入力 (state, event) → 出力 (state) のみ。 副作用（API 呼び出し / プレビュークリア / エラー表示）は一切返さない。 副作用は Content コンポーネントが「遷移後の state
 * を見て自分で起動」する。
 *
 * 設計原則:
 *
 * 1. **VERIFYING ロック**: ユーザが検証実行を開始した、またはロード時にサーバ状態が verifying だった場合、 ローカル FSM は VERIFYING に入る。この状態から抜けられるのは以下のみ:
 *
 *    - リフレッシュで「ターミナル系（結果あり）」が確定したとき → RESULT_READY
 *    - データ種別切替 (KIND_CODE_CHANGE) / タブ再入 (TAB_ENTER) / 再保存 (RESAVE_DONE) → INIT
 *
 *    これにより、リフレッシュ中にサーバが verifying 以外の非ターミナル系（verification-pending / configuring / null 等）を 一時的に返しても、
 *    ローカルの「検証実行中」ロックを誤って解除しない。
 * 2. **リフレッシュボタンは VERIFYING / REFRESH_FETCHING のみ表示**。RESULT_READY 等では非表示。 再検証したい場合は「検証実行」ボタンを使う。
 * 3. **REFRESH_FETCHING 中の status 応答処理**:
 *
 *    - `verifying` 継続 → VERIFYING に戻すだけ（結果取得不要）
 *    - ターミナル系（verification-error / import-pending / imported / reimport-pending） → REFRESH_FETCHING 維持。 コーディネータが続けて
 *         fetchValidationResult → REFRESH_RESULT_OK を発火する。
 *    - それ以外（verification-pending / configuring / unset / null 等） → **無視して VERIFYING を保持**。 これがリフレッシュフローの最重要不変条件。
 */

export const FSM = Object.freeze({
  /** マウント直後／データ種別切替直後／再保存直後で `dataKindStatus` 未取得。検証実行・リフレッシュ不可。 */
  INIT: 'INIT',
  /** ステータス取得済、検証実行可能（プレビュー未取得）。UI 層で進捗・データ確定をさらに AND する。 */
  READY: 'READY',
  /** ステータス `configuring`。検証前の設定段階で実行不可。 */
  BLOCKED: 'BLOCKED',
  /** `startValidation` API 呼び出し中（楽観的に `verifying` をセット）。 */
  STARTING: 'STARTING',
  /** サーバ側で検証実行中。リフレッシュボタン表示、検証実行ボタン無効。 */
  VERIFYING: 'VERIFYING',
  /** リフレッシュ押下中（status 再取得 → ターミナル系なら結果取得）。 */
  REFRESH_FETCHING: 'REFRESH_FETCHING',
  /** 結果あり（ターミナル系: verification-error / import-pending / imported / reimport-pending）。再検証は「検証実行」ボタンから。 */
  RESULT_READY: 'RESULT_READY',
  /** ロード系 API 失敗。タブ再入で復帰可。 */
  ERROR: 'ERROR'
})

export const EV = Object.freeze({
  /** `urlParams.genericKindCode` 変化。state を INIT に戻し dataKindStatus=null。 */
  KIND_CODE_CHANGE: 'KIND_CODE_CHANGE',
  /** マッピング設定タブ → 検証タブへの切替時のマーカー。INIT に戻して再ロード前提。 */
  TAB_ENTER: 'TAB_ENTER',
  /** 検証実行ボタン押下。楽観的に dataKindStatus=verifying をセット、STARTING へ。 */
  CLICK_START: 'CLICK_START',
  /** StartValidation 成功 → VERIFYING へ。 */
  START_OK: 'START_OK',
  /** StartValidation 失敗。payload.prevStatus へ巻き戻し、deriveSteadyState で次状態を決める。 */
  START_FAIL: 'START_FAIL',
  /** リフレッシュボタン押下。REFRESH_FETCHING へ。 */
  CLICK_REFRESH: 'CLICK_REFRESH',
  /** FetchDataKindStatus 成功（payload.status）。詳細は reducer 参照。 */
  REFRESH_STATUS_OK: 'REFRESH_STATUS_OK',
  /** FetchDataKindStatus 失敗。VERIFYING に戻す（ロック維持）。 */
  REFRESH_STATUS_FAIL: 'REFRESH_STATUS_FAIL',
  /** FetchValidationResult 成功 → RESULT_READY へ。 */
  REFRESH_RESULT_OK: 'REFRESH_RESULT_OK',
  /** FetchValidationResult 失敗。VERIFYING に戻す（ロック維持）。 */
  REFRESH_RESULT_FAIL: 'REFRESH_RESULT_FAIL',
  /** LoadValidationResult 内の status 取得成功。INIT から steady state へ。 */
  LOAD_STATUS_OK: 'LOAD_STATUS_OK',
  /** LoadValidationResult 失敗 → ERROR へ。 */
  LOAD_FAIL: 'LOAD_FAIL',
  /** マッピング再保存完了。state を INIT に戻して再ロードを促す（verifying ロック解除）。 */
  RESAVE_DONE: 'RESAVE_DONE'
})

/** 結果データを保持してプレビューを表示するべきターミナル状態の集合。 */
const TERMINAL_WITH_RESULT = Object.freeze(['verification-error', 'import-pending', 'imported', 'reimport-pending'])
const TERMINAL_SET = new Set(TERMINAL_WITH_RESULT)

/** リフレッシュ／初回ロード時に「結果一覧 API も叩く」と判定するステータス集合。 */
export const STATUSES_REQUIRING_RESULT_FETCH = new Set(TERMINAL_WITH_RESULT)

export const INITIAL_VALIDATION_FSM = Object.freeze({
  state: FSM.INIT,
  dataKindStatus: null
})

/**
 * `dataKindStatus` から「停止系の FSM 状態」を導出する。 STARTING / REFRESH_FETCHING / ERROR のような遷移途中ステートは含まない。
 *
 * - `null` → INIT（ステータス未取得）
 * - `configuring` → BLOCKED（検証以前の設定段階）
 * - `verifying` → VERIFYING（サーバ側で検証中）
 * - ターミナル系 → RESULT_READY（結果あり、再検証は「検証実行」ボタン経由）
 * - それ以外（verification-pending 等） → READY（検証実行可）
 *
 * @param {string | null | undefined} dataKindStatus
 * @returns {string}
 */
export function deriveSteadyState(dataKindStatus) {
  if (dataKindStatus == null) return FSM.INIT
  if (dataKindStatus === 'configuring') return FSM.BLOCKED
  if (dataKindStatus === 'verifying') return FSM.VERIFYING
  if (TERMINAL_SET.has(dataKindStatus)) return FSM.RESULT_READY
  return FSM.READY
}

/**
 * 純関数 FSM reducer。副作用なし。
 *
 * @param {{ state: string; dataKindStatus: string | null }} s
 * @param {{ type: string; payload?: any }} ev
 * @returns {{ state: string; dataKindStatus: string | null }}
 */
export function validationFsmReducer(s, ev) {
  switch (ev.type) {
    case EV.KIND_CODE_CHANGE:
    case EV.TAB_ENTER:
    case EV.RESAVE_DONE:
      // データ種別切替・タブ再入・再保存完了: ステータスを未取得に戻し INIT へ。
      // 後続の loadValidationResult が LOAD_STATUS_OK を発火して steady state に落とす。
      return { state: FSM.INIT, dataKindStatus: null }

    case EV.CLICK_START:
      // 楽観的に verifying をセットし STARTING へ。
      return { state: FSM.STARTING, dataKindStatus: 'verifying' }

    case EV.START_OK:
      return { ...s, state: FSM.VERIFYING }

    case EV.START_FAIL: {
      const prev = ev.payload?.prevStatus ?? null
      return { state: deriveSteadyState(prev), dataKindStatus: prev }
    }

    case EV.CLICK_REFRESH:
      // VERIFYING からのみ遷移する想定（UI 側でガードする）。dataKindStatus はそのまま保持する。
      return { ...s, state: FSM.REFRESH_FETCHING }

    case EV.REFRESH_STATUS_OK: {
      const fetched = ev.payload?.status ?? null

      // 1) ターミナル系（結果あり）→ REFRESH_FETCHING 維持、コーディネータが続けて結果取得を行う
      if (TERMINAL_SET.has(fetched)) {
        return { state: FSM.REFRESH_FETCHING, dataKindStatus: fetched }
      }

      // 2) verifying 継続 → VERIFYING に戻す（dataKindStatus も verifying のまま）
      if (fetched === 'verifying') {
        return { state: FSM.VERIFYING, dataKindStatus: 'verifying' }
      }

      // 3) それ以外（verification-pending / configuring / unset / null 等）→
      //    リフレッシュ中の非ターミナル応答はローカルロックを解除しない。
      //    ユーザが意図的に開始した検証実行を、サーバの一時的な非ターミナル応答で打ち切ってしまうのを防ぐ。
      //    ロック解除が必要なケース（再保存等）は別イベント（RESAVE_DONE / KIND_CODE_CHANGE / TAB_ENTER）で扱う。
      return { state: FSM.VERIFYING, dataKindStatus: s.dataKindStatus ?? 'verifying' }
    }

    case EV.REFRESH_STATUS_FAIL:
    case EV.REFRESH_RESULT_FAIL:
      // リフレッシュ系 API 失敗時もロックを維持して VERIFYING に戻す。
      // ユーザはエラー表示を確認した上で再度リフレッシュ可能。
      return { state: FSM.VERIFYING, dataKindStatus: s.dataKindStatus ?? 'verifying' }

    case EV.REFRESH_RESULT_OK:
      return { ...s, state: FSM.RESULT_READY }

    case EV.LOAD_STATUS_OK: {
      // 初回ロード／タブ再入後のロード。INIT から呼ばれることを前提に、フェッチした status をそのまま反映する。
      // VERIFYING ロックは適用しない（ロードはユーザの「最新を見たい」意図なので、サーバ値が正）。
      const fetched = ev.payload?.status ?? null
      return { state: deriveSteadyState(fetched), dataKindStatus: fetched }
    }

    case EV.LOAD_FAIL:
      return { ...s, state: FSM.ERROR }

    default:
      return s
  }
}
